import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileSearch, Workflow, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

export const AuditPage = () => {
  const { t, language } = useLanguage();

  const auditAreas = [
    {
      icon: Shield,
      title: t('auditPolicyTitle'),
      description: t('auditPolicyDesc'),
    },
    {
      icon: FileSearch,
      title: t('auditToolsTitle'),
      description: t('auditToolsDesc'),
    },
    {
      icon: Workflow,
      title: t('auditWorkflowTitle'),
      description: t('auditWorkflowDesc'),
    },
    {
      icon: AlertTriangle,
      title: t('auditRiskTitle'),
      description: t('auditRiskDesc'),
    },
  ];

  const deliverables = [
    t('auditReport'),
    t('auditRoadmap'),
    t('auditBenchmark'),
    t('auditPresentation'),
  ];

  const seoTitle = language === 'fr' 
    ? "Audit Stratégie IA | Gaius - Conseil Cabinets"
    : "AI Strategy Audit | Gaius - Law Firm Consulting";
    
  const seoDescription = language === 'fr'
    ? "Audit complet de votre stratégie IA : politiques, outils, workflows et risques. Pour cabinets d'avocats en France et Common Law."
    : "Comprehensive AI strategy audit: policies, tools, workflows and risks. For law firms in France and Common Law jurisdictions.";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={language === 'fr' ? "audit ia cabinet avocat, stratégie ia juridique, conformité ia droit, conseil ia law firm" : "law firm ai audit, legal ai strategy, ai compliance legal, ai consulting law firm"}
        canonicalUrl="https://gaius-tech.com/audit"
        alternateUrlFr="https://gaius-tech.com/audit"
        alternateUrlEn="https://gaius-tech.com/audit"
      />
      
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="max-w-3xl">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                {t('auditPageTitle')}
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {t('auditPageSubtitle')}
              </p>
            </div>
            <div className="mt-12 lg:mt-0">
              <img 
                src="/images/seo-audit-strategie-ia-juridique.webp"
                alt={language === 'fr'
                  ? "Audit stratégie IA juridique pour cabinets d'avocats - Intelligence artificielle droit, avocat augmenté, conseil stratégique IA"
                  : "Legal AI strategy audit for law firms - Artificial intelligence law, augmented lawyer, AI strategic consulting"}
                title={language === 'fr'
                  ? "Gaius - Audit de stratégie intelligence artificielle pour cabinets d'avocats et directions juridiques"
                  : "Gaius - AI strategy audit for law firms and legal departments"}
                loading="lazy"
                width={1200}
                height={630}
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Audit */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-foreground mb-12 text-center">
            {t('auditWhatTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {auditAreas.map((area, index) => (
              <Card key={index} className="bg-secondary/30 border-border hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <area.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl text-foreground">{area.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground">
                    {area.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                {language === 'fr' ? 'Couverture Géographique' : 'Geographic Coverage'}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                {language === 'fr'
                  ? 'Notre expertise couvre à la fois le droit français et les juridictions de Common Law (Royaume-Uni, Irlande, États-Unis), permettant une approche véritablement internationale de votre stratégie IA.'
                  : 'Our expertise covers both French law and Common Law jurisdictions (UK, Ireland, US), enabling a truly international approach to your AI strategy.'
                }
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  🇫🇷 France
                </span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  🇬🇧 UK
                </span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  🇮🇪 Ireland
                </span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  🇺🇸 US
                </span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  🇪🇺 EU
                </span>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <Card className="bg-white border-border">
                <CardHeader>
                  <CardTitle className="text-xl text-foreground">
                    {t('auditDeliverables')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {deliverables.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-foreground mb-12 text-center">
            {language === 'fr' ? 'Notre Processus' : 'Our Process'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: language === 'fr' ? 'Cadrage' : 'Scoping', desc: language === 'fr' ? 'Définition des objectifs et du périmètre' : 'Defining objectives and scope' },
              { step: '02', title: language === 'fr' ? 'Analyse' : 'Analysis', desc: language === 'fr' ? 'Évaluation approfondie de l\'existant' : 'In-depth assessment of current state' },
              { step: '03', title: language === 'fr' ? 'Diagnostic' : 'Diagnosis', desc: language === 'fr' ? 'Identification des gaps et opportunités' : 'Identifying gaps and opportunities' },
              { step: '04', title: language === 'fr' ? 'Recommandations' : 'Recommendations', desc: language === 'fr' ? 'Plan d\'action priorisé' : 'Prioritized action plan' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary/20 mb-4">{item.step}</div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-6">
            {language === 'fr' 
              ? 'Évaluez votre maturité IA'
              : 'Assess Your AI Readiness'
            }
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8">
            {language === 'fr'
              ? 'Contactez-nous pour discuter de vos enjeux et recevoir une proposition personnalisée.'
              : 'Contact us to discuss your challenges and receive a customized proposal.'
            }
          </p>
          <Button asChild size="lg" variant="secondary" className="text-lg px-8">
            <Link to="/contact" className="flex items-center">
              {t('requestAudit')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
